import { useId, useLayoutEffect, useRef, useState } from 'react'
import type { CSSProperties } from 'react'
import { useTranslation } from 'react-i18next'
import { rowActionLabel } from '../../lib/deleteLabel'

interface Props {
  /** Gösterilecek düz metin. */
  text: string
  /** Kırpma sınırı (satır). Varsayılan 4. */
  lines?: number
  /** Açık mı? **Denetimli**: tek-açık davranışı için durumu ÜST bileşen tutar. */
  expanded: boolean
  /** Düğmeye basıldığında çağrılır. */
  onToggle: () => void
  /**
   * Metnin ait olduğu kaydın adı (ör. görev başlığı). Verilirse düğmenin **erişilebilir adı** bununla
   * kurulur — bkz. {@link rowActionLabel}. Listelerde <b>verilmelidir</b>: aksi hâlde aynı sayfadaki
   * her düğme aynı adı taşır ve ekran okuyucu/sesli kontrol kullanıcısı hangi satıra ait olduğunu
   * ayırt edemez.
   */
  itemName?: string | null
}

/**
 * Uzun metni belirli satırda kırpar ve **yalnız gerçekten taşıyorsa** "Devamını gör" düğmesi çizer.
 *
 * <p><b>Neden kaydırma kutusu değil (18–21.09.2026):</b> önceki çözüm hücreyi
 * `max-height` + `overflow: auto` ile kaydırılabilir yapıyordu. Bu, açık bir erişilebilirlik boşluğu
 * bırakıyordu — kaydırılabilir bir `div` kendiliğinden odaklanabilir değildir (Chrome odaklamaz), yani
 * <b>klavye-only görsel kullanıcı dördüncü satırdan sonrasını göremiyordu</b> (WCAG 2.1.1; axe
 * `scrollable-region-focusable`). `tabIndex={0}` eklemek denenip geri alınmıştı: Sonar
 * `typescript:S6845` "tabIndex yalnız interaktif elemanda olur" deyip RELIABILITY etkisi üretiyor ve
 * THY'nin `/release` kapısının tek koşulu Reliability Rating = A — yani o hâl merge'i blokluyordu.
 * Gerçek bir `<button>` iki kapıyı da aynı anda geçer.
 *
 * <p><b>Ölçüm neden DOM'dan yapılıyor:</b> "4 satırdan uzun mu" sorusu karakter sayısıyla
 * yanıtlanamaz — kolon genişliğine, yazı tipine ve tarayıcı yakınlaştırmasına bağlıdır. Bu yüzden
 * `scrollHeight > clientHeight` ölçülür ve kolon genişliği değiştiğinde {@link ResizeObserver} ile
 * yeniden ölçülür. `useLayoutEffect` (─ `useEffect` değil): ölçüm boyamadan ÖNCE biter, aksi hâlde
 * ilk karede düğmeler yok, sonraki karede topluca beliriyordu.
 *
 * <p><b>Düğme metnin ALTINDA, kendi satırında durur.</b> Son satırın sonuna (metnin devamı gibi)
 * almak 21.09.2026'da denendi ve <b>geri alındı</b>: `float` ile yer ayırmak düğmeyi son satırın
 * hizasına indiriyor ama metinle arasında satır sonu ragged boşluğu kalıyor ve kopuk görünüyordu.
 * Boşluksuz görünmesi metni JavaScript ile ölçüp tam kırpma noktasından kesmeyi gerektirir
 * (her hücre için ikili arama) — maliyeti kazancını aşıyor, istenmedi.
 */
export default function ExpandableText({
  text,
  lines = 4,
  expanded,
  onToggle,
  itemName,
}: Readonly<Props>) {
  const { t } = useTranslation()
  const textRef = useRef<HTMLDivElement>(null)
  const [overflowing, setOverflowing] = useState(false)
  const textId = useId()

  useLayoutEffect(() => {
    const el = textRef.current
    if (!el) return undefined

    const measure = () => {
      /*
       * Ölçüm YALNIZ kırpılmış hâlde anlamlıdır: genişletilmişken kırpma kalktığı için
       * scrollHeight === clientHeight olur ve düğme kendi kendini gizlerdi (kullanıcı bir daha
       * kapatamazdı).
       *
       * BİLİNÇLİ SINIR: metin, hücre AÇIKKEN değişirse `overflowing` bayatlar (arka planda veri
       * tazelenmesi). Kalıcı bir yanlışlık değil — hücre kapandığı anda (kendi düğmesiyle ya da
       * başka satır açılınca) bu efekt yeniden koşar ve ölçüm DOM'dan tazelenir. Kapatmadan
       * ölçebilmek için kırpmayı geçici geri koyup yeniden ölçmek gerekirdi; bu ekranda görüntüleme
       * sırasında canlı güncelleme olmadığı için maliyeti gerekçelendirmiyor.
       */
      if (expanded) return
      // +1 tolerans: alt piksel yuvarlaması taşma olmadan da 1px fark üretebiliyor.
      setOverflowing(el.scrollHeight > el.clientHeight + 1)
    }

    measure()

    // jsdom'da ResizeObserver yok; testte ölçüm tek seferliktir, bu yüzden varlığı koşullu.
    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(measure)
    observer.observe(el)
    return () => observer.disconnect()
  }, [text, expanded, lines])

  const baseKey = expanded ? 'common.expandableText.less' : 'common.expandableText.more'

  return (
    <div className="ui-expandable">
      <div
        ref={textRef}
        id={textId}
        className={`ui-expandable-text${expanded ? '' : ' ui-expandable-text--clamped'}`}
        style={{ '--ui-expand-lines': lines } as CSSProperties}
      >
        {text}
      </div>
      {overflowing && (
        <button
          type="button"
          className="ui-expandable-toggle"
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={textId}
          /*
           * Erişilebilir ad SATIRIN ADINI taşır. Görünen metin kısa kalır ("Devamını gör"), ama aynı
           * listedeki onlarca düğme ekran okuyucu/sesli kontrolde birbirinden ayırt edilemezdi —
           * erişilebilir ad orada düğmenin TEK tanımlayıcısıdır. Depodaki yerleşik kalıp kullanılır
           * (`rowActionLabel`); ad boşsa adsız anahtara düşer.
           */
          aria-label={rowActionLabel(t, baseKey, itemName)}
          title={rowActionLabel(t, baseKey, itemName)}
        >
          {t(baseKey)}
          <i
            className={`pi ${expanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
            aria-hidden="true"
          />
        </button>
      )}
    </div>
  )
}
